import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import SOURCE_TYPE, {
  DEFAULT_SUB_SOURCE_TYPES
} from '../SourceCreate.constants';
import { useSourceCreateState, useSourceDialog } from '../SourceCreate.hooks';
import { mapDynamicFormValidationRules } from '../SourceCreate.service';
import { selectedSubSourceTypeAtom } from '../SourceCreate.store';

/**
 * Hook that manages sub-source type selection and its side effects.
 *
 * Sub-source types allow a single source to have multiple API versions or
 * authentication methods. Selection affects:
 * - Which connection input fields are shown
 * - Available category options
 * - Default category pre-selection
 */
const useSubSourceTypeSelector = () => {
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext();

  const [selectedSubSourceType, setSelectedSubSourceType] = useAtom(
    selectedSubSourceTypeAtom
  );

  const { mode } = useSourceDialog();

  const {
    selectedSourceTypeId,
    connectionInputSchema,
    setConnectionInputSchema
  } = useSourceCreateState();

  const hasError = !!errors?.sub_source_type;
  const errorMessage = errors?.sub_source_type?.message as string;

  /**
   * Set default sub-source type when creating a new source.
   *
   * Only applies in "New" mode - Edit mode preserves the existing selection.
   */
  useEffect(() => {
    if (mode !== 'New') return;

    const defaultValue = DEFAULT_SUB_SOURCE_TYPES[selectedSourceTypeId];
    if (defaultValue) {
      setSelectedSubSourceType(defaultValue);
      setValue('sub_source_type', defaultValue);
    }
  }, [selectedSourceTypeId, mode, setSelectedSubSourceType, setValue]);

  /**
   * Update connection schema when sub-source type changes.
   *
   * Different sub-source types expose different connection input fields.
   * For example:
   * - Mimecast Version2 uses source_inputs_v2, Version1 uses source_inputs_v1
   * - Cisco Endpoint "Yes" uses new queue config, "No" uses existing queue
   * - Qualys "Session" vs "Basic" have different authentication fields
   */
  useEffect(() => {
    let sourceInputs = null;

    if (selectedSourceTypeId === SOURCE_TYPE.MIMECAST) {
      if (selectedSubSourceType === 'Version2') {
        sourceInputs = connectionInputSchema?.source_inputs_v2;
      } else {
        sourceInputs = connectionInputSchema?.source_inputs_v1;
      }
    } else if (selectedSourceTypeId === SOURCE_TYPE.CISCO_SECURE_ENDPOINT) {
      if (selectedSubSourceType === 'Yes') {
        sourceInputs = connectionInputSchema?.source_inputs_new_queue;
      } else {
        sourceInputs = connectionInputSchema?.source_inputs_existing_queue;
      }
    } else if (selectedSourceTypeId === SOURCE_TYPE.QUALYS) {
      const connInput = {
        ...connectionInputSchema,
        source_inputs:
          selectedSubSourceType === 'Session'
            ? connectionInputSchema?.source_inputs_v1
            : connectionInputSchema?.source_inputs_v2
      };
      setConnectionInputSchema(mapDynamicFormValidationRules(connInput));
    }

    if (sourceInputs) {
      setConnectionInputSchema(
        mapDynamicFormValidationRules({
          ...connectionInputSchema,
          source_inputs: sourceInputs
        })
      );
    }
  }, [
    selectedSourceTypeId,
    selectedSubSourceType,
    connectionInputSchema?.source_inputs_v1,
    connectionInputSchema?.source_inputs_v2,
    connectionInputSchema?.source_inputs_new_queue,
    connectionInputSchema?.source_inputs_existing_queue,
    setConnectionInputSchema
  ]);

  /**
   * Extract UI configuration from connection input schema.
   *
   * The backend provides sub_source_type_inputs with form field metadata.
   * We extract the first (and typically only) configuration.
   */
  const subSourceTypeConfig = useMemo(() => {
    const config = connectionInputSchema?.sub_source_type_inputs?.[0];
    return {
      defaultValue: config?.value ?? '',
      type: config?.type ?? '',
      label: config?.label ?? '',
      options: config?.options ?? []
    };
  }, [connectionInputSchema]);

  /**
   * Determine whether to render the sub-source type selector.
   *
   * Only specific source types support sub-source types, and only when
   * the backend schema explicitly enables it via show_sub_source_type
   * or by providing sub_source_type_inputs.
   */
  const shouldRenderSubSourceType = useMemo(() => {
    const supportedSourceTypes = [
      SOURCE_TYPE.MIMECAST,
      SOURCE_TYPE.CISCO_SECURE_ENDPOINT,
      SOURCE_TYPE.QUALYS
    ];

    const isSourceTypeSupported =
      supportedSourceTypes.includes(selectedSourceTypeId);

    const hasSchemaSupport =
      connectionInputSchema?.show_sub_source_type ||
      connectionInputSchema?.sub_source_type_inputs;

    return isSourceTypeSupported && hasSchemaSupport;
  }, [selectedSourceTypeId, connectionInputSchema]);

  return {
    control,
    type: subSourceTypeConfig.type,
    options: subSourceTypeConfig.options,
    defaultValue: subSourceTypeConfig.defaultValue,
    label: subSourceTypeConfig.label,
    hasError,
    errorMessage,
    shouldRenderSubSourceType,
    mode
  };
};

export default useSubSourceTypeSelector;
